import React from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, CreditCard } from 'lucide-react';
import { formatDate } from '@/lib/format';
import { SubscriptionData } from '@/services/subscriptionService';

interface CurrentSubscriptionProps {
  subscription: SubscriptionData | null;
  onManageBilling?: () => void;
}

// Status badge component for subscription status
const StatusBadge = ({ status }: { status: string }) => {
  const getStatusProps = () => {
    switch (status) {
      case 'active':
        return { className: 'bg-green-100 text-green-800 border-green-200', label: 'Active' };
      case 'trialing':
        return { className: 'bg-blue-100 text-blue-800 border-blue-200', label: 'Trial' };
      case 'past_due':
        return { className: 'bg-amber-100 text-amber-800 border-amber-200', label: 'Past Due' };
      case 'canceled':
        return { className: 'bg-red-100 text-red-800 border-red-200', label: 'Canceled' };
      default:
        return { className: 'bg-gray-100 text-gray-800 border-gray-200', label: status };
    }
  };

  const { className, label } = getStatusProps();
  return <Badge className={className}>{label}</Badge>;
};

export const CurrentSubscription: React.FC<CurrentSubscriptionProps> = ({ 
  subscription,
  onManageBilling
}) => {
  // Return early if subscription is null or undefined
  if (!subscription) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>No Active Subscription</CardTitle>
          <CardDescription>You don't have an active subscription.</CardDescription>
        </CardHeader>
        <CardContent>
          <p>Select a plan below to get started.</p>
        </CardContent>
      </Card>
    );
  }
  
  const isTrial = subscription.status === 'trialing';
  const isActive = subscription.status === 'active';
  const isPastDue = subscription.status === 'past_due';
  const isCanceled = subscription.status === 'canceled' || subscription.cancel_at;

  const formatDateRelative = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.ceil((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'today';
    if (diffDays === 1) return 'tomorrow';
    if (diffDays === -1) return 'yesterday';
    if (diffDays > 0 && diffDays < 30) return `in ${diffDays} days`;
    if (diffDays < 0 && diffDays > -30) return `${Math.abs(diffDays)} days ago`;
    
    return formatDate(dateString);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between">
        <div>
          <CardTitle>Current Plan</CardTitle>
          <CardDescription>Your subscription details</CardDescription>
        </div>
        <StatusBadge status={subscription.status} />
      </CardHeader>
      
      <CardContent className="space-y-4">
        <div>
          <h4 className="font-medium">Plan Details</h4>
          <div className="mt-1">
            <Badge className="bg-primary/10 text-primary border-primary/20">
              Essential Plan
            </Badge>
          </div>
        </div>

        {isTrial && subscription.trial_end && (
          <div className="flex items-center">
            <Calendar className="h-4 w-4 mr-2 text-gray-500" />
            <div className="text-sm">
              Trial ends {formatDateRelative(subscription.trial_end)}
            </div>
          </div>
        )}

        {isActive && subscription.current_period_end && (
          <div className="flex items-center">
            <CreditCard className="h-4 w-4 mr-2 text-gray-500" />
            <div className="text-sm">
              Next billing date: {formatDateRelative(subscription.current_period_end)}
            </div>
          </div>
        )}

        {isCanceled && subscription.cancel_at && (
          <div className="flex items-center">
            <Calendar className="h-4 w-4 mr-2 text-red-500" />
            <div className="text-sm text-red-600">
              Subscription ends {formatDateRelative(subscription.cancel_at)}
            </div>
          </div>
        )}

        {isPastDue && (
          <div className="bg-red-50 p-3 rounded-md border border-red-200">
            <p className="text-sm text-red-700">
              Your payment is past due. Please update your payment information to avoid service interruption.
            </p>
          </div>
        )}
      </CardContent>

    </Card>
  );
};